import {
  Component,
  inject,
  input,
  output,
  model,
  forwardRef,
  signal,
  computed,
  ElementRef,
  viewChild,
  HostListener,
  AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { isSafari } from '../../utilities/browser.util';
import { SafeSelectRegistryService } from './safe-select-registry.service';

export interface SafeSelectOption {
  value: any;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
}

@Component({
  selector: 'app-safe-select',
  imports: [NzIconModule, NzSelectModule, FormsModule],
  templateUrl: './safe-select.html',
  styleUrls: ['./safe-select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SafeSelectComponent),
      multi: true,
    },
  ],
})
export class SafeSelectComponent implements ControlValueAccessor, AfterViewInit {
  options = input.required<SafeSelectOption[]>();
  value = model<any>(null);
  placeholder = input<string>('');
  disabled = input<boolean>(false);
  showSearch = input<boolean>(true);
  allowClear = input<boolean>(false);
  compareWith = input<(o1: any, o2: any) => boolean>((o1, o2) => o1 === o2);
  selectedClass = input<boolean>(false);
  iconClass = input<string>('');
  valueChange = output<any>();

  readonly isSafari = isSafari();
  private readonly registry = inject(SafeSelectRegistryService);

  // Search state for Safari
  searchText = signal('');
  dropdownOpen = signal(false);
  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  filteredOptions = computed(() => {
    const text = this.searchText().toLowerCase();
    if (!text) return this.options().filter((o) => !o.hidden);
    return this.options().filter((o) => !o.hidden && o.label.toLowerCase().includes(text));
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const el = event.target as HTMLElement;
    if (!el.closest('.native-search-select-wrapper')) {
      this.close();
    }
  }

  ngAfterViewInit() {
    document.addEventListener(
      'scroll',
      (event: Event) => {
        const target = event.target as HTMLElement;
        // Ignore scrolling inside nz-select dropdown
        if (target?.closest('.cdk-overlay-pane')) {
          return;
        }
        document.body.click();
      },
      true
    );
  }

  openDropdown() {
    if (!this.isDisabled) {
      this.registry.open(this);
      this.dropdownOpen.set(true);
    }
  }

  toggleDropdown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDisabled) {
      if (this.dropdownOpen()) {
        this.close();
      } else {
        this.registry.open(this);
        this.dropdownOpen.set(true);
        this.searchInput()?.nativeElement.focus();
      }
    }
  }

  close() {
    if (!this.dropdownOpen()) return;
    this.dropdownOpen.set(false);
    this.searchInput()?.nativeElement.blur();
    this.registry.closed(this);
  }

  selectSearchOption(option: SafeSelectOption) {
    this.value.set(option.value);
    this.valueChange.emit(option.value);
    this.onChangeFn?.(option.value);
    this.onTouchedFn?.();
    this.searchText.set('');
    this.close();
  }

  get selectedLabel(): string {
    const current = this.value();
    const compareFn = this.compareWith();
    const found = this.options().find((opt) => compareFn(opt.value, current));
    return found?.label ?? '';
  }

  // ControlValueAccessor
  private onChangeFn?: (val: any) => void;
  private onTouchedFn?: () => void;

  cvaDisabled = false;

  writeValue(val: any): void {
    this.value.set(val);
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }

  get isDisabled(): boolean {
    return this.disabled() || this.cvaDisabled;
  }

  onNzChange(val: any) {
    this.value.set(val);
    this.valueChange.emit(val);
    this.onChangeFn?.(val);
    this.onTouchedFn?.();
  }

  onNativeChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const index = parseInt(target.value, 10);
    const options = this.options();

    if (isNaN(index) || index < 0 || index >= options.length) {
      this.value.set(null);
      this.valueChange.emit(null);
      this.onChangeFn?.(null);
      return;
    }

    const selected = options[index].value;
    this.value.set(selected);
    this.valueChange.emit(selected);
    this.onChangeFn?.(selected);
    this.onTouchedFn?.();
  }

  getNativeSelectedIndex(): number {
    const current = this.value();
    const compareFn = this.compareWith();
    return this.options().findIndex((opt) => compareFn(opt.value, current));
  }

  onClear() {
    this.value.set(null);
    this.valueChange.emit(null);
    this.onChangeFn?.(null);
    this.onTouchedFn?.();
  }
}
